﻿export interface IBoxModelRectangle {
	top: number;
	left: number;
	height: number;
	width: number;
	right: number;
	bottom: number;
	center: DOMPoint;
}

type DOMSpacing = {
	bottom: number
	left: number
	right: number
	top: number
}

type BoxModelData = {
	/// <summary>
	///     Valores del borde (border-top, border-right, border-bottom, border-left)
	/// </summary>
	border: DOMSpacing

	/// <summary>
	///     posición de la caja que contiene content + padding + border
	/// </summary>
	borderBox: IBoxModelRectangle

	/// <summary>
	///     posición de la caja que contiene content
	/// </summary>
	contentBox: IBoxModelRectangle;

	/// <summary>
	///     Valores del margen (margin-top, margin-right, margin-bottom, margin-left)
	/// </summary>
	margin: DOMSpacing;

	/// <summary>
	///     posición de la caja que contiene content + padding + border + margin
	/// </summary>
	marginBox: IBoxModelRectangle;

	/// <summary>
	///     Valores del padding (padding-top, padding-right, padding-bottom, padding-left)
	/// </summary>
	padding: DOMSpacing;

	/// <summary>
	///     posición de la caja que contiene content + padding
	/// </summary>
	paddingBox: IBoxModelRectangle;
}


const empty: DOMSpacing = {top: 0, bottom: 0, left: 0, right: 0};
const scrollTest: RegExp = /(auto|scroll|overlay)/;
const positionTest: RegExp = /(absolute|fixed)/;

function parse(value: string): number {
	return parseInt(value, 10);
}

function expand(target: DOMSpacing, expandBy: DOMSpacing): DOMSpacing {
	return {
		top: target.top - expandBy.top,
		left: target.left - expandBy.left,
		bottom: target.bottom + expandBy.bottom,
		right: target.right + expandBy.right
	};
}

function shrink(target: DOMSpacing, shrinkBy: DOMSpacing): DOMSpacing {
	return {
		top: target.top + shrinkBy.top,
		left: target.left + shrinkBy.left,
		bottom: target.bottom - shrinkBy.bottom,
		right: target.right - shrinkBy.right
	};
}

function getRect(spacing: DOMSpacing): IBoxModelRectangle {
	const top = spacing.top;
	const right = spacing.right;
	const bottom = spacing.bottom;
	const left = spacing.left;

	return {
		top: top,
		right: right,
		bottom: bottom,
		left: left,
		width: right - left,
		height: bottom - top,
		center: new DOMPoint((right + left) / 2, (bottom + top) / 2)
	};
}

function createBox(borderBox: DOMSpacing, margin?: DOMSpacing, padding?: DOMSpacing, border?: DOMSpacing): BoxModelData {
	margin = margin ?? empty;
	padding = padding ?? empty;
	border = border ?? empty;

	const marginBox = getRect(expand(borderBox, margin));
	const paddingBox = getRect(shrink(borderBox, border));
	const contentBox = getRect(shrink(paddingBox, padding));

	return {
		marginBox: marginBox,
		borderBox: getRect(borderBox),
		paddingBox: paddingBox,
		contentBox: contentBox,
		margin: margin!,
		border: border!,
		padding: padding!
	};

}

function calculateBox(borderBox: DOMRect, styles: CSSStyleDeclaration): BoxModelData {
	const margin: DOMSpacing = {
		top: parse(styles.marginTop),
		right: parse(styles.marginRight),
		bottom: parse(styles.marginBottom),
		left: parse(styles.marginLeft)
	};

	const padding: DOMSpacing = {
		top: parse(styles.paddingTop),
		right: parse(styles.paddingRight),
		bottom: parse(styles.paddingBottom),
		left: parse(styles.paddingLeft)
	};

	const border: DOMSpacing = {
		top: parse(styles.borderTopWidth),
		right: parse(styles.borderRightWidth),
		bottom: parse(styles.borderBottomWidth),
		left: parse(styles.borderLeftWidth)
	};

	return createBox(borderBox, margin, padding, border);
}

function getParentBy(element: HTMLElement, evaluator: ((declaration: CSSStyleDeclaration) => boolean)): HTMLElement {
	const style = window.getComputedStyle(element);
	const excludeStaticParent = style.position == "absolute";
	const body = document.body;

	if (style.position == "fixed") {
		return body;
	}

	let current: HTMLElement | null = element;
	do {
		const innerStyle = current === element ? style : window.getComputedStyle(current);

		if ((!excludeStaticParent || innerStyle.position != "static") && evaluator(innerStyle)) {
			return current;
		}

		current = current.parentElement;
	} while (current != null && current != body);

	return body;
}

function shift(spacing: DOMSpacing, point: DOMPointReadOnly): DOMSpacing {
	return {
		top: spacing.top + point.y,
		left: spacing.left + point.x,
		bottom: spacing.bottom + point.y,
		right: spacing.right + point.x
	}
}

function offset(original: BoxModelData, change: DOMPointReadOnly): BoxModelData {
	const doc = document.documentElement;
	const updated = new DOMPointReadOnly(change.x - doc.clientLeft, change.y - doc.clientTop);
	return createBox(shift(original.borderBox, updated), original.margin, original.padding, original.border);
}

function getCurrentScrollPosition(element: HTMLElement | null): DOMPointReadOnly {
	if (element != null) {
		return new DOMPointReadOnly(element.scrollLeft, element.scrollTop);
	}

	return new DOMPointReadOnly(window.scrollX, window.scrollY);
}

export function getBoxModel(element: HTMLElement): BoxModelData {
	const borderBox = element.getBoundingClientRect();
	const styles = window.getComputedStyle(element);
	return calculateBox(borderBox, styles);
}

export function getBoxModelWithScroll(element: HTMLElement, scrollParent?: HTMLElement): BoxModelData;
export function getBoxModelWithScroll(original: BoxModelData, scrollParent?: HTMLElement): BoxModelData;
export function getBoxModelWithScroll(element: HTMLElement, scrollPosition?: DOMPointReadOnly): BoxModelData;

export function getBoxModelWithScroll(element: any, parent: any): BoxModelData {
	let original: BoxModelData;
	let scrollPosition: DOMPointReadOnly | null = null;

	if (element instanceof HTMLElement) {
		original = getBoxModel(element);
		if (parent instanceof HTMLElement) {
			scrollPosition = new DOMPointReadOnly(parent.scrollLeft, parent.scrollTop);
		} else {
			scrollPosition = parent;
		}
	} else {
		original = element;
	}

	return offset(original, scrollPosition ?? getCurrentScrollPosition(null));
}


export function getParentScrollable(element: HTMLElement): HTMLElement {
	return getParentBy(element, declaration => scrollTest.test(declaration.overflow + declaration.overflowY + declaration.overflowX));
}

export function getPositioningContainer(element: HTMLElement): HTMLElement {
	return getParentBy(element, declaration => positionTest.test(declaration.position));
}
